import { QueryClient } from "@tanstack/react-query";
import { isRedirect, redirect } from "@tanstack/react-router";

import { reschedulePreviewQuery } from "@/modules/coaching/shared/queries/getReschedulePreviewQuery";
import { ROUTES } from "@/modules/shared/consts/routes";
import { DEFAULT_ENTRY_POINT } from "@/modules/tracking/consts/EntryPoints";
import { SHORT_URL_EVENTS } from "@/modules/tracking/consts/Events";
import { ShortUrlFlow } from "@/modules/tracking/consts/ShortUrl";
import { abandonPreviousShortUrlFlowIfDifferent } from "@/modules/tracking/utils/abandonPreviousShortUrlFlow";
import {
  getShortUrlResolutionFromError,
  getShortUrlResolutionFromSuccess,
} from "@/modules/tracking/utils/getShortUrlResolutionStatus";
import { identifyMember } from "@/modules/tracking/utils/identifyMember";
import { readSourceFromLocation } from "@/modules/tracking/utils/readSourceFromLocation";
import { registerSuperProperties } from "@/modules/tracking/utils/registerSuperProperties";
import {
  endShortUrlFlowSession,
  isSameShortUrlFlowVisit,
  startShortUrlFlowSession,
} from "@/modules/tracking/utils/shortUrlFlowSession";
import { trackShortUrlEvent } from "@/modules/tracking/utils/trackShortUrlEvent";

type RescheduleSessionLoaderArgs = {
  context: { queryClient: QueryClient };
  params: { rescheduleUuid: string };
};

export const rescheduleSessionLoader = async ({
  context: { queryClient },
  params: { rescheduleUuid },
}: RescheduleSessionLoaderArgs) => {
  const source = readSourceFromLocation();
  const isRevalidation = isSameShortUrlFlowVisit(
    ShortUrlFlow.Reschedule,
    rescheduleUuid,
  );

  try {
    const data = await queryClient.ensureQueryData(
      reschedulePreviewQuery(rescheduleUuid),
    );

    registerSuperProperties({
      entry_point: DEFAULT_ENTRY_POINT,
      customer_id: data.customer.id,
    });

    identifyMember({ email: data.customer.email });

    if (!isRevalidation) {
      abandonPreviousShortUrlFlowIfDifferent(
        ShortUrlFlow.Reschedule,
        rescheduleUuid,
      );

      const resolution = getShortUrlResolutionFromSuccess();
      trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
        access_code: rescheduleUuid,
        session_id: String(data.sessionId),
        coach_id: String(data.coach.id),
        resolution_status: resolution.resolution_status,
        http_status: resolution.http_status,
        correlation_id: null,
      });

      startShortUrlFlowSession({
        flow: ShortUrlFlow.Reschedule,
        accessCode: rescheduleUuid,
        sessionId: String(data.sessionId),
        source,
        correlationId: null,
      });

      trackShortUrlEvent(SHORT_URL_EVENTS.FLOW_ENTERED, {
        flow: ShortUrlFlow.Reschedule,
        session_id: String(data.sessionId),
        coach_id: String(data.coach.id),
        source,
      });
    }

    return { data };
  } catch (error) {
    if (isRedirect(error)) {
      throw error;
    }

    if (!isRevalidation) {
      const resolution = getShortUrlResolutionFromError(error);
      trackShortUrlEvent(SHORT_URL_EVENTS.ACCESS_CODE_RESOLVED, {
        access_code: rescheduleUuid,
        session_id: null,
        coach_id: null,
        resolution_status: resolution.resolution_status,
        http_status: resolution.http_status,
        correlation_id: null,
      });
    }
    endShortUrlFlowSession();

    throw redirect({ to: ROUTES.notFound });
  }
};
